import React, { useMemo, useState } from "react";
import ConditionalRender from "../../../ConditionalRender.js";
import { Icons } from "../Icons/index.js";
import Tooltip from "../../../components/cta_utils/Tooltip.js";

// ─── Password validation rules ──────────────────────────────────────────
// Each key maps to a regex pattern. Used by validatePassword() below.

const passwordRules = {
  length: /.{8,}/,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  digit: /[0-9]/,
  special_characters: /[!"#$%&'()*+,-./\\:;<=>?@[\]^_`{|}~]/,
};

/** Human-readable labels for each rule (shown in the strength tooltip) */
const formatRules = {
  length: "Minimum 8 characters",
  uppercase: "One uppercase letter (A-Z)",
  lowercase: "One lowercase letter (a-z)",
  digit: "One number (0-9)",
  special_characters: "One special character (!@#$)",
};

/**
 * Validates a password against all rules.
 * Returns an object where keys are rule names that FAILED.
 * An empty object means the password passes all rules.
 */
export const validatePassword = (password) => {
  const errors = {};
  if (!password) return errors;
  Object.keys(passwordRules).forEach((rule) => {
    const format = passwordRules[rule];
    if (!format.test(password)) errors[rule] = true;
  });
  return errors;
};

const InputBoxSizes = {
  Small: "small",
  Medium: "medium",
  Large: "large",
};

/**
 * PasswordBox — Password input with show/hide toggle and strength tooltip.
 *
 * Shows a tooltip on focus displaying which password rules are met (green check)
 * vs not met (red cross). Supports external error validation or auto-validation.
 *
 * @param {string}  Size            - 'Small' | 'Medium' | 'Large'
 * @param {string}  value           - Controlled password value
 * @param {boolean} defaultVisible  - Start with password visible (default: hidden)
 * @param {boolean} showToggle      - Show the eye icon toggle button
 * @param {boolean} tooltipEnable   - Show the password strength tooltip on focus
 * @param {Object}  errors          - External validation errors (overrides auto-validation)
 * @param {func}    handleOnChange  - Change handler
 */
export const PasswordBox = ({
  Size = "Medium",
  label = "",
  showLabel = false,
  labelClass = "input-box-label body-s-semibold",
  showHelperText = false,
  helperText = "",
  helperTextClass = "helper-text support-m",
  helperTextStyle = {},
  inputStyle = {},
  value,
  placeholder = "password",
  customClass = "",
  customStyle = {},
  handleOnChange,
  handleOnKeyDown,
  error = "",
  disabled = false,
  defaultVisible = false,
  showToggle = true,
  toggleIconShow = "eye-show",
  toggleIconHide = "eye-hide",
  inputProps = {},
  tooltipEnable = true,
  name,
  errors,
}) => {
  const [visible, setVisible] = useState(defaultVisible);
  const [showToolTip, setShowToolTip] = useState(false);
  const inputSize = InputBoxSizes[Size];

  const ruleSize = Object.keys(formatRules).length;

  // Use external errors if provided, otherwise auto-validate the current value
  const computedErrors = useMemo(() => errors ?? validatePassword(value), [errors, value]);

  // Memoize class string to avoid string concatenation on every render
  const computedInputClassName = useMemo(
    () =>
      [
        "input-box",
        "input-box--has-trailing-icon",
        `input-box-${inputSize}`,
        error && "input-box-error",
        disabled && "input-box-disabled",
        customClass,
      ]
        .filter(Boolean)
        .join(" "),
    [customClass, disabled, error, inputSize]
  );

  return (
    <div style={{ ...customStyle }}>
      <ConditionalRender if={showLabel && label !== ""}>
        <div className={labelClass}>{label}</div>
      </ConditionalRender>

      <div style={{ position: "relative", width: "100%", ...inputStyle }}>
        <Tooltip
          tooltipText={() =>
            Object.keys(formatRules).map((rule, i) => (
              <div
                key={rule}
                className="flex items-center"
                style={{ marginBottom: i !== ruleSize - 1 ? 'var(--space-s)' : 0 }}
              >
                {/* Green check if rule passes, red cross if it fails */}
                <Icons name={computedErrors?.[rule] ? "error_status" : "success_status"} size={16} style={{ width: 12, height: 12 }} alt="*" />
                <div
                  className="body-s-semibold"
                  style={{ color: "white", lineHeight: "12px", marginLeft: 'var(--space-xs)' }}
                >
                  {formatRules[rule]}
                </div>
              </div>
            ))
          }
          enableTooltip={tooltipEnable && showToolTip && !!value}
          showTooltip={showToolTip}
          innerClass="text_body_light"
          textTheme="background-light-black right"
          innerStyle={{ padding: "12px", alignSelf: "center", color: "white", borderRadius: "8px" }}
          showWithoutOverflowing={true}
          tooltipPos="right"
          tooltTipOverlay={{ x: 7, y: -36 }}
        >
          <div style={{ position: "relative", width: "100%" }}>
            <input
              className={computedInputClassName}
              disabled={disabled}
              type={visible ? "text" : "password"}
              value={value}
              name={name}
              placeholder={placeholder}
              onChange={handleOnChange}
              onKeyDown={handleOnKeyDown}
              onFocus={() => setShowToolTip(true)}
              onBlur={() => setShowToolTip(false)}
              {...inputProps}
            />

            {/* Show/hide toggle button */}
            <ConditionalRender if={showToggle}>
              <button
                type="button"
                className="input-box__trailing-icon"
                onMouseDown={(e) => e.preventDefault()} // Prevent blur on toggle click
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? "Hide password" : "Show password"}
                disabled={disabled}
              >
                <Icons name={visible ? toggleIconHide : toggleIconShow} size={24} />
              </button>
            </ConditionalRender>
          </div>
        </Tooltip>
      </div>

      <ConditionalRender if={showHelperText && helperText !== ""}>
        <div className={helperTextClass} style={helperTextStyle}>
          {helperText}
        </div>
      </ConditionalRender>
    </div>
  );
};
